import React, { createContext, useContext, useState, useEffect } from 'react';
import usePatentData from '@/hooks/use-patent-data';
import { PriorArtTypes } from '@/helpers/constants';

const ChartPreparationContext = createContext();

export const ChartPreparationProvider = ({ children }) => {
  const [chartPreparationData, setChartPreparationData] = useState({
    analysisType: 'invalidity',
    selectedPriorArts: [],
    selectedClaimRowKeys: [],
    selectedOpalClaimRowKeys: [],
    targetSearchType: 'patent',
    targetPatentNum: '',
    targetPatentData: null,
    targetPatentDataLoading: false,
    targetManualTitle: '',
    targetManualClaim: '',
    targetManualDescription: '',
    targetManualElements: [],
    elements: [],
    manualPatentNum: '',
    manualPatentData: null,
    manualPatentDataLoading: false,
    opalFilters: {country: ['US'], parentCompany: [], assigneeCurrent: []},
    opalPatentNum: '',
    opalPatentData: null,
    opalPatentDataLoading: false,
    opalPatentElements: [],
    recommendedPatentNum: '',
    recommendedPatentData: null,
    recommendedPatentDataLoading: false,
    manualEntry: { title: '', description: '' },
    priorArtType: PriorArtTypes.PATENT,
  });

  const patentDataInfo = [
    { num: 'targetPatentNum', data: 'targetPatentData', language: 'targetPatentLanguage', loading: 'targetPatentDataLoading' },
    { num: 'manualPatentNum', data: 'manualPatentData', language: 'manualPatentLanguage', loading: 'manualPatentDataLoading' },
    { num: 'recommendedPatentNum', data: 'recommendedPatentData', language: 'recommendedPatentLanguage', loading: 'recommendedPatentDataLoading' },
    { num: 'opalPatentNum', data: 'opalPatentData', language: 'opalPatentLanguage', loading: 'opalPatentDataLoading' },
  ];

  const getDescriptionByLanguage = (data, language) => {
    const description = data?.description?.find(desc => desc.lang === language);
    return description ? description.text : data?.description?.[0]?.text;
  };
  
  patentDataInfo.forEach(({ num, data, language, loading }) => {
    const { data: fetchedData, loading: fetchedLoading } = usePatentData(chartPreparationData[num], true);
    const langCode = fetchedData?.defaultLanguage;
    const description = getDescriptionByLanguage(fetchedData, langCode);

    useEffect(() => {
      setChartPreparationData((prev) => {
        if (chartPreparationData[num]) {
          const updatedData = { ...prev, [loading]: fetchedLoading, [language]: langCode };
          if (description) {
            updatedData[data] = { ...fetchedData, description };
          } else {
            updatedData[data] = fetchedData;
          }
          return updatedData;
        }
        return prev;
      });
    }, [fetchedData, fetchedLoading, chartPreparationData[num]]);
  });

  const updateAnalysisType = (analysisType) => {
    setChartPreparationData((prev) => ({ ...prev, analysisType, priorArtType: analysisType === 'invalidity' ? PriorArtTypes.PATENT : PriorArtTypes.STANDARD }));
  };

  const updateTargetSearchType = (targetSearchType) => {
    setChartPreparationData((prev) => ({ ...prev, targetSearchType }));
  };

  const updateSelectedClaimRowKeys = (selectedClaimRowKeys) => {
    setChartPreparationData((prev) => ({ ...prev, selectedClaimRowKeys }));
  };

  const updateSelectedOpalClaimRowKeys = (selectedOpalClaimRowKeys) => {
    setChartPreparationData((prev) => ({ ...prev, selectedOpalClaimRowKeys }));
  };

  const updateTargetPatentNum = (targetPatentNum) => {
    setChartPreparationData((prev) => {
      if (targetPatentNum !== prev.targetPatentNum) {
        return { ...prev, targetPatentNum, targetPatentData: null, elements: [] };
      }
      return { ...prev, targetPatentNum };
    });
  };

  const updateTargetManualTitle = (targetManualTitle) => {
    setChartPreparationData((prev) => ({ ...prev, targetManualTitle }));
  };

  const updateTargetManualClaim = (targetManualClaim) => {
    setChartPreparationData((prev) => ({ ...prev, targetManualClaim }));
  };

  const updateTargetManualDescription = (targetManualDescription) => {
    setChartPreparationData((prev) => ({ ...prev, targetManualDescription }));
  };

  const updateTargetManualElements = (targetManualElements) => {
    setChartPreparationData((prev) => ({ ...prev, targetManualElements }));
  };

  const updateElements = (elements) => {
    setChartPreparationData((prev) => ({ ...prev, elements }));
  };

  const updatePriorArtType = (priorArtType) => {
    setChartPreparationData((prev) => ({ ...prev, priorArtType }));
  };

  const updateManualPatentNum = (manualPatentNum) => {
    setChartPreparationData((prev) => {
      if (manualPatentNum !== prev.manualPatentNum) {
        return { ...prev, manualPatentNum, manualPatentData: null };
      }
      return { ...prev, manualPatentNum };
    });
  };

  const updateRecommendedPatentNum = (recommendedPatentNum) => {
    setChartPreparationData((prev) => {
      if (recommendedPatentNum !== prev.recommendedPatentNum) {
        return { ...prev, recommendedPatentNum, recommendedPatentData: null };
      }
      return { ...prev, recommendedPatentNum };
    });
  };

  const updateOpalFilters = (opalFilters) => {
    setChartPreparationData((prev) => ({ ...prev, opalFilters }));
  };

  const updateOpalPatentNum = (opalPatentNum) => {
    setChartPreparationData((prev) => {
      if (opalPatentNum !== prev.opalPatentNum) {
        return { ...prev, opalPatentNum, opalPatentData: null };
      }
      return { ...prev, opalPatentNum };
    });
  };

  const updateOpalPatentElements = (opalPatentElements) => {
    setChartPreparationData((prev) => ({ ...prev, opalPatentElements }));
  };

  const updateManualEntry = (title, description) => {
    setChartPreparationData((prev) => ({
      ...prev,
      manualEntry: { title, description },
    }));
  };

  const addPriorArt = (priorArt) => {
    setChartPreparationData((prev) => ({
      ...prev,
      selectedPriorArts: [...prev.selectedPriorArts, priorArt],
    }));
  };

  const handleShortTitleChange = (index, newShortTitle) => {
    setChartPreparationData((prev) => ({
      ...prev,
      selectedPriorArts: prev.selectedPriorArts.map((art, idx) => 
        idx === index ? { ...art, shortTitle: newShortTitle } : art
      ),
    }));
  };

  const handlePriorArtCitationOptionsChange = (index, field, checked) => {
    setChartPreparationData((prev) => ({
      ...prev,
      selectedPriorArts: prev.selectedPriorArts.map((art, idx) => 
        idx === index ? { ...art, citationOptions: { ...art.citationOptions, [field]: checked } } : art
      ),
    }));
  };

  const deletePriorArtByIndex = (index) => {
    setChartPreparationData((prev) => ({
      ...prev,
      selectedPriorArts: prev.selectedPriorArts.filter((_, i) => i !== index),
    }));
  };

  const resetPriorArtSelector = () => {
    setChartPreparationData((prev) => ({
      ...prev,
      manualPatentNum: '',
      manualPatentData: null,
      opalPatentNum: '',
      opalPatentData: null,
      recommendedPatentNum: '',
      recommendedPatentData: null,
      manualEntry: { title: '', description: '' },
      priorArtType: prev.analysisType === 'invalidity' ? PriorArtTypes.PATENT : PriorArtTypes.STANDARD,
      uploadedFileText: '',
    }));
  };

  const restoreChartPreparationData = (data) => {
    setChartPreparationData((prev) => {
      // Add citationOptions for patent types that don't have them
      const updatedData = { ...data };
      if (updatedData.selectedPriorArts) {
        updatedData.selectedPriorArts = updatedData.selectedPriorArts.map(priorArt => {
          if (priorArt.publication_number && !priorArt.citationOptions) {
            return {
              ...priorArt,
              citationOptions: {
                abstract: true,
                claims: true,
                description: true
              }
            };
          }
          return priorArt;
        });
      }
      return {
        ...prev,
        ...updatedData,
      };
    });
  }; 

  return (
    <ChartPreparationContext.Provider value={{
      chartPreparationData,
      restoreChartPreparationData,
      updateAnalysisType,
      updateSelectedClaimRowKeys,
      updateSelectedOpalClaimRowKeys,
      updateTargetSearchType,
      updateTargetPatentNum,
      updateTargetManualTitle,
      updateTargetManualClaim,
      updateTargetManualDescription,
      updateTargetManualElements,
      updateElements,
      updatePriorArtType,
      updateManualPatentNum,
      updateRecommendedPatentNum,
      updateOpalPatentNum,
      updateOpalFilters,
      updateOpalPatentElements,
      updateManualEntry,
      addPriorArt,
      handleShortTitleChange,
      handlePriorArtCitationOptionsChange,
      deletePriorArtByIndex,
      resetPriorArtSelector,
    }}>
      {children}
    </ChartPreparationContext.Provider>
  );
};

export const useChartPreparation = () => useContext(ChartPreparationContext);
